/* eslint-disable max-len */
import {
    Component, Input, OnChanges, OnInit, ViewEncapsulation, SimpleChanges, Output, EventEmitter, TemplateRef, Signal,
    inject,
    computed,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { ProductFilterItem, UpdateFilterObj } from '../../../menu-categories/models/menu-categories.model';

import { Category } from '@core/domain/models/category.models';
import { getDebugBarShowCategoryIdsStatus, getLanguageisRTL } from '@core/infrastructure/state/core.selectors';
import { CategoriesSelectors } from '@features/category';
import { checkAttachmentName, findCategoryById } from '@features/category/application/services/category.utils';
import { SeoSelectors } from '@features/seo';
import { CategoryFilterState, filterGroupArrayNames } from '@pages/category'; // TODO ECOMDUTI-25790
import { DisplayFilters } from '@shared/ab-testing/models/AB-testing.models';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { CategoryNameInfo, ToolBarCategory } from '@shared/category-toolbar/models/category-tool-bar.model';
import * as CategoryToolBarUseCase from '@shared/category-toolbar/use-cases/category-toolbar.use-case';
import { MenuCategory } from '@shared/header/models/category.model';

@Component({
    selector: 'category-sub-categories-selector',
    templateUrl: './category-sub-categories-selector.component.html',
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
})

/**
 * Componente para mostrar las subcategorías de una categoria en parrillas
 */
export class CategorySubCategoriesSelectorComponent implements OnInit, OnChanges {
    private readonly store = inject(Store);

    private readonly abTestingService = inject(ABTestingService);

    @Input() public category: ToolBarCategory;

    @Input() public selectGridView: TemplateRef<any>;

    @Input() public filterType$: Observable<DisplayFilters>;

    @Input() public isRtl$: Observable<boolean>;

    @Input() public filters: CategoryFilterState;

    @Input() public currentCategory: Category;

    @Input() public filterTypeUsed: filterGroupArrayNames;

    @Output() public readonly updateFilterEmitter: EventEmitter<UpdateFilterObj> = new EventEmitter();

    @Output() public readonly categoryNameInfoEmitter: EventEmitter<CategoryNameInfo> = new EventEmitter();

    public showDebugCategoryIds$$: Signal<boolean> = this.store.selectSignal(getDebugBarShowCategoryIdsStatus);

    public lastSectionTranslated$$: Signal<string> = this.store.selectSignal(SeoSelectors.getLastSectionTranslated);

    public direction$$: Signal<string> = computed(() => this.store.selectSignal(getLanguageisRTL)() ? 'rtl' : 'ltr');

    public siblingCategories: MenuCategory[];

    public categoryName: string;

    public subCategories: Category[] | MenuCategory[] = [];

    public currentCategoryFromABTest: MenuCategory;

    public filtersArray: ProductFilterItem[];

    public parentCategory: MenuCategory;

    public showFilters = false;

    public isSpecialCollection = false;

    public showCategoryTitle = true;

    public isNormalBehavior = true;

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.category?.currentValue) {
            this.initializeCategories();
        }
    }

    public ngOnInit(): void {
        this.initializeCategories();
    }

    private initializeCategories(): void {
        const { currentCategory, categories = [], subCategories = [] } = this.category ?? {};
        const categorySiblingsParent = checkAttachmentName(currentCategory, 'show_subcategories_grid');
        const categorySiblings = checkAttachmentName(currentCategory, 'special_collection')
            || checkAttachmentName(currentCategory, 'special_collection_limited');
        const cmsLookbook = checkAttachmentName(currentCategory, 'cms-lookbook-app');

        this.isSpecialCollection = categorySiblings || categorySiblingsParent;
        this.showCategoryTitle = !checkAttachmentName(currentCategory, 'hide_PLP_title_APPS');

        if (this.category) {
            const isSpecialCollection = this.isSpecialCollection || cmsLookbook;

            if (isSpecialCollection) {
                this.setComponentInfoForSpecialCollection({ currentCategory, categories, subCategories }, categorySiblingsParent);
            } else {
                this.setComponentInfo({ currentCategory, categories, subCategories });
            }
        }
    }

    private setComponentInfo(menuCategory: ToolBarCategory): void {
        const menuCategories$$ = this.store.selectSignal(CategoriesSelectors.getMenuCategories);

        const _cat = { ...menuCategory };
        const currentCategoryId = this.currentCategory?.id;

        // Recuperamos la categoría actual de las categorías del menú con todos sus attachments transformados
        _cat.currentCategory = findCategoryById(menuCategory.categories, currentCategoryId);

        const category = menuCategories$$()?.find(cat => cat.id === _cat.currentCategory?.parentId);
        if (category) {
            _cat.subCategories = category.subcategories;
            _cat.categories = menuCategories$$();
        }

        this.currentCategoryFromABTest = this.abTestingService.getCategoryRenamed(CategoryToolBarUseCase.SetCurrentCategoryNameUseCase(_cat));
        this.filtersArray = CategoryToolBarUseCase.GetFiltersAsSubcategoriesUseCase(this.filterTypeUsed, this.filters);
        this.isNormalBehavior = this.filtersArray?.length === 0;
        this.currentCategoryFromABTest.isNew = CategoryToolBarUseCase.CheckIfCategoryIsNewUseCase(this.currentCategoryFromABTest);
        this.siblingCategories = CategoryToolBarUseCase.GetSiblingsUseCase(_cat, this.currentCategoryFromABTest);
        this.showFilters = CategoryToolBarUseCase.SetFilterVisibilityUseCase(this.currentCategory);

        const renameCms = _cat.currentCategory?.name !== this.currentCategory?.name ? _cat.currentCategory?.name : '';
        this.categoryName = CategoryToolBarUseCase.SetCategoryNameUseCase(this.currentCategory, this.currentCategoryFromABTest, renameCms);

        this.updateCategoryName([], 'text-l ttu m-0');
    }

    private setComponentInfoForSpecialCollection(menuCategory: ToolBarCategory, categorySiblingsParent: boolean): void {
        let currentCategory = this.category.currentCategory;
        if (currentCategory) this.parentCategory = this.category.subCategories?.find(subcategory => subcategory.id === currentCategory.id);

        const categoriesNavigation$$ = this.store.selectSignal(CategoriesSelectors.getCategoriesNavigation);
        const categoriesNavigation = categoriesNavigation$$();

        currentCategory = categoriesNavigation.find(category => category.id === menuCategory.currentCategory.id);

        this.parentCategory = categoriesNavigation.find(category => category.id === currentCategory.parentId);

        const targetCategory = categorySiblingsParent ? this.parentCategory : currentCategory;
        this.siblingCategories = CategoryToolBarUseCase.GetSiblingCategoriesSpecialCollectionUseCase(categoriesNavigation, targetCategory);
        this.subCategories = categorySiblingsParent ? this.parentCategory.subCategoryIds.map(id => categoriesNavigation.find(category => category.id === id)) : targetCategory.subcategories;

        const categoryForABTest = { currentCategory: currentCategory, categories: categoriesNavigation, subCategories: this.subCategories };
        this.currentCategoryFromABTest = this.abTestingService.getCategoryRenamed(CategoryToolBarUseCase.SetCurrentCategoryNameUseCase(categoryForABTest));

        this.showFilters = CategoryToolBarUseCase.SetFilterVisibilityUseCase(currentCategory, true);
        this.categoryName = CategoryToolBarUseCase.SetCategoryNameUseCase(currentCategory, this.currentCategoryFromABTest, '');

        this.updateCategoryName(this.subCategories, 'pt-8 pb-8 align-middle text-l ttu');
    }

    public updateCategoryName(subCategories: MenuCategory[] | Category[], classes: string): void {
        this.categoryNameInfoEmitter.emit({
            subCategories: subCategories,
            currentCategoryFromABTest: this.currentCategoryFromABTest,
            categoryName: this.categoryName,
            h1TagClasses: classes,
        });
    }

    public updateFilter(updateFilterObj: UpdateFilterObj): void {
        this.updateFilterEmitter.emit(updateFilterObj);
    }
}
